import { useEffect, useState } from 'react';
import { api } from '../api/shop';
import { useAuth } from '../auth/AuthContext';

/**
 * The heart. SOW §2.7.
 *
 * OPTIMISTIC, and safe to be. The API is idempotent on both sides — PUT twice is
 * one row, DELETE of nothing succeeds — so the fill can happen on the tap and a
 * double tap cannot produce an error to undo. The only rollback is a genuine
 * failure, which puts the heart back where it was and says so.
 *
 * SIGNED OUT, IT IS NOT SHOWN AT ALL. A heart that prompts for sign-in when
 * tapped teaches people that tapping things costs them a detour; a heart that
 * silently does nothing is worse. There is no local-storage favourites list
 * either — that would be a second source of truth that quietly disagrees with
 * the server the moment they sign in on another device.
 */
export function FavoriteButton({
  businessId,
  businessName,
  initial,
  onChange,
}: {
  businessId: string;
  businessName: string;
  initial: boolean;
  onChange?: (isFavorite: boolean) => void;
}) {
  const { userId, ready } = useAuth();
  const [on, setOn] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // useState(initial) reads its argument ONCE, at mount. On the deal page the
  // favourites list arrives after the deal does, so without this the heart would
  // sit empty on a business the customer had already saved — and tapping it
  // would look like it worked while sending a redundant PUT.
  //
  // Guarded on `busy` so a reply landing mid-flight cannot stamp on the
  // optimistic state of a toggle still in the air.
  useEffect(() => {
    if (!busy) setOn(initial);
  }, [initial, busy]);

  // `ready` matters as much as `userId`: until the stored session has been
  // checked, a signed-in customer is momentarily indistinguishable from a
  // signed-out one, and rendering nothing then popping a heart in is worse than
  // waiting a frame.
  if (!ready || userId === null) return null;

  async function toggle() {
    const next = !on;

    setOn(next);
    setBusy(true);
    setFailed(false);
    onChange?.(next);

    try {
      await (next ? api.addFavorite(businessId) : api.removeFavorite(businessId));
    } catch {
      setOn(!next);
      setFailed(true);
      onChange?.(!next);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={on ? 'favbtn on' : 'favbtn'}
        onClick={() => void toggle()}
        disabled={busy}
        // The NAME is in the label, not just "favourite". On a page of twelve
        // cards, twelve buttons all announcing "Add to favourites" tell a screen
        // reader user nothing about which one they are on.
        aria-label={on
          ? `Remove ${businessName} from your favourites`
          : `Save ${businessName} to your favourites`}
        aria-pressed={on}
      >
        {/* aria-hidden: the button already has a label, and the heart would
            otherwise be announced as a second, meaningless piece of text. */}
        <span aria-hidden="true">{on ? '♥' : '♡'}</span>
      </button>

      {/* role="alert" rather than status: the heart has just moved back on its
          own, and the customer needs to know why without hunting for it. */}
      {failed && (
        <span className="visually-hidden" role="alert">
          We could not save that. Please try again.
        </span>
      )}
    </>
  );
}
